import React, {
  useState,
  useEffect,
  useReducer,
  useRef,
  useContext
} from "react";

import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Badge,
  Tooltip
} from "@material-ui/core";

import {
  AccessTime,
  Block,
  Description,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Reply,
  Translate
} from "@material-ui/icons";

import AudioModal from "../AudioModal";
import MarkdownWrapper from "../MarkdownWrapper";
import ModalImageCors from "../ModalImageCors";
import MediaViewerModal from "../MediaViewerModal";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import LocationPreview from "../LocationPreview";
import whatsBackgroundDark from "../../assets/wa-background-dark.png"; //DARK MODE PLW DESIGN//
import { getPublicUrl } from "../../utils/runtimeUrls";
import VCardPreview from "../VCardPreview";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { SocketContext } from "../../context/Socket/SocketContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import StickerItem from "./StickerItem";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Mutex } from "async-mutex";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
const loadPageMutex = new Mutex();

export const isStickerMessage = message => {
  if (!message) return false;
  if (message.mediaType === "sticker" || message.mediaType === "stickerMessage") {
    return true;
  }
  const cleanBody = (message.body || "").trim().toLowerCase();
  const hasStickerBody = cleanBody === "sticker" || cleanBody === "figurinha";
  const url = message.mediaUrl || "";
  const isWebp = url.endsWith(".webp") || url.includes(".webp?");
  if (hasStickerBody && (url || message.mediaType === "image")) {
    return true;
  }
  if (isWebp && (hasStickerBody || !cleanBody)) {
    return true;
  }
  return false;
};

const useStyles = makeStyles(theme => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200
  },

  messagesList: {
    backgroundImage:
      theme.mode === "light"
        ? `url(${whatsBackground})`
        : `url(${whatsBackgroundDark})`, //DARK MODE PLW DESIGN//
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12
  },

  messageLeftSticker: {
    marginRight: 20,
    marginTop: 4,
    marginBottom: 4,
    minWidth: 100,
    maxWidth: 200,
    height: "auto",
    display: "block",
    position: "relative",
    alignSelf: "flex-start",
    backgroundColor: "transparent !important",
    boxShadow: "none !important",
    border: "none !important",
    padding: 0,
    borderRadius: 0,
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 2,
      right: 2,
      zIndex: 10,
      backgroundColor: "rgba(0,0,0,0.5)",
      borderRadius: "50%",
      color: "#fff"
    }
  },

  messageRightSticker: {
    marginLeft: 20,
    marginTop: 4,
    marginBottom: 4,
    minWidth: 100,
    maxWidth: 200,
    height: "auto",
    display: "block",
    position: "relative",
    alignSelf: "flex-end",
    backgroundColor: "transparent !important",
    boxShadow: "none !important",
    border: "none !important",
    padding: 0,
    borderRadius: 0,
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 2,
      right: 2,
      zIndex: 10,
      backgroundColor: "rgba(0,0,0,0.5)",
      borderRadius: "50%",
      color: "#fff"
    }
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.messageIncoming || "#ffffff",
    color: theme.palette.text.primary,
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
    transition: "background-color 0.5s ease-in-out"
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.mode === "light" ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.06)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    cursor: "pointer"
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden"
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef"
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.messageOutgoing || "#d9fdd3",
    color: theme.palette.text.primary,
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)"
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.mode === "light" ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.06)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative"
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap"
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96"
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" }
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px"
  },

  textContentItemEdited: {
    overflowWrap: "break-word",
    padding: "3px 120px 6px 6px"
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px"
  },
  forwardMessage: {
    fontSize: 12,
    fontStyle: "italic",
    position: "absolute",
    top: 0,
    left: 5,
    color: "#999",
    display: "flex",
    alignItems: "center"
  },
  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999"
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3"
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px"
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10
  },
  documentCard: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: 8,
    padding: "8px 12px",
    margin: "4px 0",
    maxWidth: 320,
    cursor: "pointer",
    transition: "background 0.2s",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.09)",
    },
  },
  documentIconBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 38,
    height: 38,
    borderRadius: 8,
    backgroundColor: "rgba(0, 168, 132, 0.15)",
    color: "#00a884",
    marginRight: 10,
    flexShrink: 0,
  },
  documentInfo: {
    flex: 1,
    minWidth: 0,
    marginRight: 8,
  },
  documentName: {
    fontSize: 13,
    fontWeight: 500,
    color: "inherit",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  documentExtBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: "#667781",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    marginTop: 2,
  },
  documentActionBtn: {
    padding: 6,
    color: "rgba(0, 0, 0, 0.55)",
    "&:hover": {
      color: "#00a884",
    },
  },
  "@global": {
    "@keyframes wave": {
      "0%, 60%, 100%": {
        transform: "initial"
      },
      "30%": {
        transform: "translateY(-15px)"
      }
    },
    "@keyframes quiet": {
      "25%": {
        transform: "scaleY(.6)"
      },
      "50%": {
        transform: "scaleY(.4)"
      },
      "75%": {
        transform: "scaleY(.8)"
      }
    },
    "@keyframes normal": {
      "25%": {
        transform: "scaleY(.1)"
      },
      "50%": {
        transform: "scaleY(.4)"
      },
      "75%": {
        transform: "scaleY(.6)"
      }
    },
    "@keyframes loud": {
      "25%": {
        transform: "scaleY(1)"
      },
      "50%": {
        transform: "scaleY(.4)"
      },
      "75%": {
        transform: "scaleY(1.2)"
      }
    }
  },
  wave: {
    position: "relative",
    textAlign: "center",
    height: "30px",
    marginTop: "10px",
    marginLeft: "auto",
    marginRight: "auto"
  },
  dot: {
    display: "inline-block",
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    marginRight: "3px",
    background: theme.mode === "light" ? "#303030" : "#ffffff",
    animation: "wave 1.3s linear infinite",
    "&:nth-child(2)": {
      animationDelay: "-1.1s"
    },
    "&:nth-child(3)": {
      animationDelay: "-0.9s"
    }
  },

  wavebarsContainer: {
    display: "flex",
    justifyContent: "space-between",
    height: "30px",
    marginTop: "5px",
    marginBottom: "5px",
    marginLeft: "auto",
    marginRight: "auto",
    "--boxSize": "5px",
    "--gutter": "4px",
    width: "calc((var(--boxSize) + var(--gutter)) * 5)"
  },

  wavebars: {
    transform: "scaleY(.4)",
    height: "100%",
    width: "var(--boxSize)",
    animationDuration: "1.2s",
    backgroundColor: theme.mode === "light" ? "#303030" : "#ffffff",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
    borderRadius: "8px"
  },

  wavebar1: {
    animationName: "quiet"
  },
  wavebar2: {
    animationName: "normal"
  },
  wavebar3: {
    animationName: "quiet"
  },
  wavebar4: {
    animationName: "loud"
  },
  wavebar5: {
    animationName: "quiet"
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach(message => {
      const messageIndex = state.findIndex(m => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex(m => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex(m => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticket, ticketId, isGroup, previewMode = false }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  const scrollRef = useRef();
  const [contactPresence, setContactPresence] = useState("available");

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const socketManager = useContext(SocketContext);
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext);

  // Media gallery viewer state
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(0);

  const [transcribing, setTranscribing] = useState({});

  const handleTranscribe = async (message) => {
    setTranscribing(prev => ({ ...prev, [message.id]: true }));
    try {
      await api.post(`/messages/${message.id}/transcribe`);
      toast.success("Mídia transcrita com sucesso!");
    } catch (err) {
      console.error("Erro ao transcrever mídia:", err);
      toast.error("Ops! Não foi possível processar a imagem. O serviço pode estar indisponível.");
    } finally {
      setTranscribing(prev => ({ ...prev, [message.id]: false }));
    }
  };

  const mediaMessages = messagesList.filter(
    (m) => (m.mediaType === "image" || m.mediaType === "video" || isStickerMessage(m)) && m.mediaUrl
  );

  const openMediaViewer = (message) => {
    const idx = mediaMessages.findIndex((m) => m.id === message.id);
    setMediaViewerIndex(idx >= 0 ? idx : 0);
    setMediaViewerOpen(true);
  };

  function loadData(incrementPage = false) {
    setLoading(true);
    const thisPageNumber = incrementPage ? pageNumber + 1 : 1;
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === undefined) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber: thisPageNumber, preview: previewMode ? "true" : "false" }
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
      setPageNumber(thisPageNumber);
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }

  useEffect(() => {
    dispatch({ type: "RESET" });

    currentTicketId.current = ticketId;

    (async () => {
      await loadPageMutex.runExclusive(async () => {
        loadData();
      });
    })();
  }, [ticketId, previewMode]);

  const ticketRef = useRef(ticket);
  useEffect(() => {
    ticketRef.current = ticket;
  }, [ticket]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleReady = () => {
      if (ticketId) {
        socket.emit("joinChatBox", `${ticketId}`);
      }
    };

    const handleAppMessage = data => {
      if (!data.message) return;

      if (
        data.action === "create" &&
        String(data.message.ticketId) === String(currentTicketId.current)
      ) {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (
        data.action === "update" &&
        String(data.message.ticketId) === String(currentTicketId.current)
      ) {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    };

    const handleContact = data => {
      const t = ticketRef.current;
      if (
        data?.contact?.id &&
        t?.contact?.id &&
        data.contact.id === t.contact.id &&
        data.action === "update"
      ) {
        setContactPresence(data.contact.presence || "available");
      }
    };

    socket.on("ready", handleReady);
    socket.on(`company-${companyId}-appMessage`, handleAppMessage);
    socket.on(`company-${companyId}-contact`, handleContact);

    return () => {
      socket.disconnect();
    };
  }, [ticketId, socketManager]);

  const loadMore = async () => {
    await loadPageMutex.runExclusive(async () => {
      loadData(true);
    });
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = e => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const hanldeReplyMessage = (e, message) => {
    //if (ticket.status === "open" || ticket.status === "group") {
    setAnchorEl(null);
    setReplyingMessage(message);
    //}
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = e => {
    setAnchorEl(null);
  };

  const getDocumentCleanName = message => {
    if (!message) return "Documento";
    let name = "";
    if (
      message.body &&
      typeof message.body === "string" &&
      message.body.includes(".") &&
      !message.body.includes("\n") &&
      message.body.length < 150
    ) {
      name = message.body;
    } else if (message.mediaUrl) {
      const parts = message.mediaUrl.split("/");
      name = parts[parts.length - 1] || "";
    }
    if (!name) return "Documento";

    name = name.replace(/^\d{10,}[-_]/, "").trim();

    name = name.replace(/\.vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet$/i, ".xlsx");
    name = name.replace(/\.sheets$/i, ".xlsx");
    name = name.replace(/\.vnd\.openxmlformats-officedocument\.wordprocessingml\.document$/i, ".docx");
    name = name.replace(/\.vnd\.openxmlformats-officedocument\.presentationml\.presentation$/i, ".pptx");

    return name || "Documento";
  };

  const getDocumentExtension = name => {
    if (!name || !name.includes(".")) return "";
    return name.slice(name.lastIndexOf(".") + 1).toUpperCase();
  };

  const handleDownloadMedia = async (e, message) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    const cleanName = getDocumentCleanName(message);
    try {
      const response = await api.get(`/messages/${message.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", cleanName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      if (message.mediaUrl) {
        const link = document.createElement("a");
        link.href = message.mediaUrl;
        link.target = "_blank";
        link.setAttribute("download", cleanName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }
    }
  };

  const shouldRenderMessageBody = message => {
    if (!message.body) return false;
    if (isStickerMessage(message)) return false;
    if (
      message.mediaType === "locationMessage" ||
      message.mediaType === "contactMessage" ||
      message.mediaType === "vcard" ||
      (typeof message.body === "string" && message.body.startsWith("\u2935"))
    ) {
      return false;
    }
    if (
      message.mediaUrl &&
      message.mediaType !== "image" &&
      message.mediaType !== "video" &&
      message.mediaType !== "audio"
    ) {
      const cleanName = getDocumentCleanName(message);
      const rawBody = message.body.trim();
      const cleanBody = rawBody.replace(/^\d{10,}[-_]/, "");
      if (
        rawBody === cleanName ||
        cleanBody === cleanName ||
        message.mediaUrl.endsWith(rawBody) ||
        rawBody.endsWith(cleanName) ||
        cleanName.endsWith(cleanBody)
      ) {
        return false;
      }
    }
    return true;
  };

  const checkMessageMedia = message => {
    if (
      message.mediaType === "locationMessage" &&
      message.body.split("|").length >= 2
    ) {
      let locationParts = message.body.split("|");
      let imageLocation = locationParts[0];
      let linkLocation = locationParts[1];

      let descriptionLocation = null;

      if (locationParts.length > 2)
        descriptionLocation = message.body.split("|")[2];

      return (
        <LocationPreview
          image={imageLocation}
          link={linkLocation}
          description={descriptionLocation}
        />
      );
    } else if (message.mediaType === "contactMessage" || message.mediaType === "vcard" || (message.body && typeof message.body === 'string' && message.body.startsWith("\u2935"))) {
      let contact = "";
      let number = "";

      if (message.body && typeof message.body === 'string' && message.body.startsWith("\u2935")) {
        const lines = message.body.split("\n").filter(l => l.trim() !== "");
        if (lines.length >= 3) {
          number = lines[1].replace(/[^\d+]/g, "");
          contact = lines.slice(2).join(" ");
        }
      } else {
        let array = message.body.split("\n");
        let obj = [];
        for (let index = 0; index < array.length; index++) {
          const v = array[index];
          let values = v.split(":");
          for (let ind = 0; ind < values.length; ind++) {
            if (values[ind].indexOf("+") !== -1) {
              obj.push({ number: values[ind] });
            } else if (values[ind].indexOf("waid=") !== -1) {
              let parts = values[ind].split("waid=");
              if (parts.length > 1) {
                let num = parts[1].split(":")[0];
                obj.push({ number: num });
              }
            } else if (v.startsWith("TEL") && ind === 1) {
                obj.push({ number: values[ind] });
            }

            if (values[ind].indexOf("FN") !== -1) {
              contact = values[ind + 1];
            }
          }
        }
        number = obj.length > 0 ? obj[0].number : "";
      }
      return <VCardPreview contact={contact} numbers={number} />;
    } else if (message.mediaType === "image") {
      return (
        <div style={{ position: "relative" }}>
          <img
            className={classes.messageMedia}
            src={message.mediaUrl}
            alt=""
            style={{ cursor: "pointer" }}
            onClick={() => openMediaViewer(message)}
          />
          <Tooltip title="Transcrever Mídia com IA">
            <IconButton
              size="small"
              onClick={() => handleTranscribe(message)}
              disabled={transcribing[message.id]}
              style={{ position: "absolute", bottom: 5, right: 5, backgroundColor: "rgba(0,0,0,0.5)", color: "white" }}
            >
              {transcribing[message.id] ? <CircularProgress size={20} color="inherit" /> : <Translate fontSize="small" />}
            </IconButton>
          </Tooltip>
        </div>
      );
    } else if (message.mediaType === "audio") {
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <AudioModal url={message.mediaUrl} />
          <Tooltip title="Transcrever Áudio com IA">
            <IconButton
              size="small"
              onClick={() => handleTranscribe(message)}
              disabled={transcribing[message.id]}
              style={{ marginLeft: 5 }}
            >
              {transcribing[message.id] ? <CircularProgress size={20} /> : <Translate fontSize="small" color="primary" />}
            </IconButton>
          </Tooltip>
        </div>
      );
    } else if (message.mediaType === "video") {
      return (
        <div style={{ position: "relative" }}>
          <video
            className={classes.messageMedia}
            src={message.mediaUrl}
            style={{ cursor: "pointer" }}
            onClick={() => openMediaViewer(message)}
            preload="metadata"
          />
          <Tooltip title="Transcrever Mídia com IA">
            <IconButton
              size="small"
              onClick={() => handleTranscribe(message)}
              disabled={transcribing[message.id]}
              style={{ position: "absolute", bottom: 5, right: 5, backgroundColor: "rgba(0,0,0,0.5)", color: "white" }}
            >
              {transcribing[message.id] ? <CircularProgress size={20} color="inherit" /> : <Translate fontSize="small" />}
            </IconButton>
          </Tooltip>
        </div>
      );
    } else {
      const cleanDocName = getDocumentCleanName(message);
      const extBadge = getDocumentExtension(cleanDocName);
      return (
        <div
          className={classes.documentCard}
          onClick={e => handleDownloadMedia(e, message)}
          title={`Baixar ${cleanDocName}`}
        >
          <div className={classes.documentIconBox}>
            <Description fontSize="small" />
          </div>
          <div className={classes.documentInfo}>
            <div className={classes.documentName} title={cleanDocName}>
              {cleanDocName}
            </div>
            {extBadge && (
              <div className={classes.documentExtBadge}>{extBadge}</div>
            )}
          </div>
          <IconButton
            size="small"
            className={classes.documentActionBtn}
            onClick={e => handleDownloadMedia(e, message)}
            title="Baixar arquivo"
          >
            <GetApp fontSize="small" />
          </IconButton>
        </div>
      );
    }
  };

  /*
    const renderMessageAck = (message) => {
      if (message.ack === 1) {
        return <AccessTime fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 2) {
        return <Done fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 3) {
        return <DoneAll fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 4 || message.ack === 5) {
        return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
      }
    };
    */

  const renderMessageAck = message => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 4 || message.ack === 5) {
      return (
        <DoneAll
          fontSize="small"
          className={classes.ackDoneAllIcon}
          style={{ color: "#0377FC" }}
        />
      );
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderNumberTicket = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageTicket = message.ticketId;
      let connectionName = message.ticket?.whatsapp?.name;
      let previousMessageTicket = messagesList[index - 1].ticketId;

      if (messageTicket !== previousMessageTicket) {
        return (
          <center>
            <div className={classes.ticketNunberClosed}>
              Conversa encerrada:{" "}
              {format(
                parseISO(messagesList[index - 1].createdAt),
                "dd/MM/yyyy HH:mm:ss"
              )}
            </div>

            <div className={classes.ticketNunberOpen}>
              Conversa iniciada:{" "}
              {format(parseISO(message.createdAt), "dd/MM/yyyy HH:mm:ss")}
            </div>
          </center>
        );
      }
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = message => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}

          {message.quotedMsg.mediaType === "audio" && (
            <div className={classes.downloadMedia}>
              <audio controls>
                <source
                  src={message.quotedMsg.mediaUrl}
                  type="audio/ogg"
                ></source>
              </audio>
            </div>
          )}
          {message.quotedMsg.mediaType === "video" && (
            <video
              className={classes.messageMedia}
              src={message.quotedMsg.mediaUrl}
              controls
            />
          )}
          {message.quotedMsg.mediaType === "application" && (
            <div className={classes.downloadMedia}>
              <Button
                startIcon={<GetApp />}
                color="primary"
                variant="outlined"
                target="_blank"
                href={message.quotedMsg.mediaUrl}
              >
                Download
              </Button>
            </div>
          )}

          {(isStickerMessage(message.quotedMsg) && (
            <img
              src={message.quotedMsg.mediaUrl}
              alt="Figurinha"
              style={{ width: 50, height: 50, objectFit: "contain" }}
            />
          )) ||
          (message.quotedMsg.mediaType === "image" && (
            <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />
          )) ||
            message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  // ── Sanitização de body para mensagens de mídia ──
  // Remove tags de sistema duplicadas e limpa o "Nenhum texto detectado" redundante
  const sanitizeMediaBody = (body) => {
    if (!body || typeof body !== 'string') return body;
    let cleaned = body;

    // Remover ocorrências duplicadas de "[Sistema: Imagem recebida...]\nNenhum texto detectado na imagem."
    const systemTagPattern = /\[Sistema: Imagem recebida, mas nenhum texto legível foi detectado\]\s*\n?Nenhum texto detectado na imagem\.\s*/g;
    const systemMatches = cleaned.match(systemTagPattern);
    if (systemMatches && systemMatches.length > 1) {
      // Remover todas exceto a última
      for (let i = 0; i < systemMatches.length - 1; i++) {
        cleaned = cleaned.replace(systemMatches[i], '');
      }
    }

    // Remover ocorrências duplicadas de "[Descrição IA da Imagem: ...]" 
    const descTagPattern = /\[Descrição IA da Imagem:.*?\]\s*/g;
    const descMatches = cleaned.match(descTagPattern);
    if (descMatches && descMatches.length > 1) {
      for (let i = 0; i < descMatches.length - 1; i++) {
        cleaned = cleaned.replace(descMatches[i], '');
      }
    }

    // Remover "Nenhum texto detectado na imagem." quando já existe uma descrição IA
    if (cleaned.includes('[Descrição IA da Imagem:')) {
      cleaned = cleaned.replace(/Nenhum texto detectado na imagem\.\s*/g, '');
    }

    return cleaned.trim();
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (message.mediaType === "call_log") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageCenter}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={e => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 17"
                    width="20"
                    height="17"
                  >
                    <path
                      fill="#df3333"
                      d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"
                    ></path>
                  </svg>{" "}
                  <span>
                    Chamada de voz/vídeo perdida às{" "}
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        const isSticker = isStickerMessage(message);

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={isSticker ? classes.messageLeftSticker : classes.messageLeft}
                title={message.queueId && message.queue?.name}
                onDoubleClick={e => hanldeReplyMessage(e, message)}
              >
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox
                    // showSelectMessageCheckbox={showSelectMessageCheckbox}
                    message={message}
                    // selectedMessagesList={selectedMessagesList}
                    // setSelectedMessagesList={setSelectedMessagesList}
                  />
                )}
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={e => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}>
                      <Reply
                        style={{ color: "grey", transform: "scaleX(-1)" }}
                      />{" "}
                      Encaminhada
                    </span>
                    <br />
                  </div>
                )}
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}

                {/* aviso de mensagem apagado pelo contato */}
                {message.isDeleted && (
                  <div>
                    <span className={"message-deleted"}>
                      Essa mensagem foi apagada pelo contato &nbsp;
                      <Block
                        color="error"
                        fontSize="small"
                        className={classes.deletedIcon}
                      />
                    </span>
                  </div>
                )}

                {isSticker ? (
                  <>
                    {message.quotedMsg && renderQuotedMessage(message)}
                    <StickerItem
                      message={message}
                      renderMessageAck={renderMessageAck}
                      openMediaViewer={openMediaViewer}
                    />
                  </>
                ) : (
                  <>
                    {(message.mediaUrl ||
                      message.mediaType === "locationMessage" ||
                      message.mediaType === "vcard" ||
                      message.mediaType === "contactMessage" ||
                      (message.body && typeof message.body === 'string' && message.body.startsWith("\u2935"))) &&
                      //|| message.mediaType === "multi_vcard"
                      checkMessageMedia(message)}
                    <div className={classes.textContentItem}>
                      {message.quotedMsg && renderQuotedMessage(message)}
                      {message.mediaType !== "reactionMessage" && shouldRenderMessageBody(message) && (
                        <MarkdownWrapper>
                          {sanitizeMediaBody(message.body)}
                        </MarkdownWrapper>
                      )}
                      {message.quotedMsg &&
                        message.mediaType === "reactionMessage" &&
                        message.body && (
                          <>
                            <span
                              style={{
                                marginLeft: "0px",
                                display: "flex",
                                alignItems: "center"
                              }}
                            >
                              <MarkdownWrapper>
                                {"_*" +
                                  (message.fromMe
                                    ? "Você"
                                    : message?.contact?.name ?? "Contato") +
                                  "*_ reagiu... "}
                              </MarkdownWrapper>
                              <Badge
                                className={classes.badge}
                                overlap="circular"
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "right"
                                }}
                                badgeContent={
                                  <span
                                    style={{
                                      fontSize: "1.2em",
                                      marginTop: "0",
                                      marginLeft: "5px"
                                    }}
                                  >
                                    {message.body}
                                  </span>
                                }
                              ></Badge>
                            </span>
                          </>
                        )}

                      <span className={classes.timestamp}>
                        {message.isEdited
                          ? "Editada " +
                            format(parseISO(message.createdAt), "HH:mm")
                          : format(parseISO(message.createdAt), "HH:mm")}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={isSticker ? classes.messageRightSticker : classes.messageRight}
                onDoubleClick={e => hanldeReplyMessage(e, message)}
              >
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox
                    // showSelectMessageCheckbox={showSelectMessageCheckbox}
                    message={message}
                    // selectedMessagesList={selectedMessagesList}
                    // setSelectedMessagesList={setSelectedMessagesList}
                  />
                )}
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={e => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}>
                      <Reply
                        style={{ color: "grey", transform: "scaleX(-1)" }}
                      />{" "}
                      Encaminhada
                    </span>
                    <br />
                  </div>
                )}
                {isSticker ? (
                  <>
                    {message.quotedMsg && renderQuotedMessage(message)}
                    <StickerItem
                      message={message}
                      renderMessageAck={renderMessageAck}
                      openMediaViewer={openMediaViewer}
                    />
                  </>
                ) : (
                  <>
                    {(message.mediaUrl ||
                      message.mediaType === "locationMessage" ||
                      message.mediaType === "vcard" ||
                      message.mediaType === "contactMessage" ||
                      (message.body && typeof message.body === 'string' && message.body.startsWith("\u2935"))) &&
                      //|| message.mediaType === "multi_vcard"
                      checkMessageMedia(message)}
                    <div
                      className={clsx(classes.textContentItem, {
                        [classes.textContentItemDeleted]: message.isDeleted
                      })}
                    >
                      {message.isDeleted && (
                        <Block
                          color="disabled"
                          fontSize="small"
                          className={classes.deletedIcon}
                        />
                      )}
                      {message.quotedMsg && renderQuotedMessage(message)}
                      {message.mediaType !== "reactionMessage" &&
                        message.mediaType !== "locationMessage" &&
                        shouldRenderMessageBody(message) && (
                          <MarkdownWrapper>
                            {sanitizeMediaBody(message.body)}
                          </MarkdownWrapper>
                        )}
                      {message.quotedMsg &&
                        message.mediaType === "reactionMessage" &&
                        message.body && (
                          <>
                            <span
                              style={{
                                marginLeft: "0px",
                                display: "flex",
                                alignItems: "center"
                              }}
                            >
                              <MarkdownWrapper>
                                {"_*" +
                                  (message.fromMe
                                    ? "Você"
                                    : message?.contact?.name ?? "Contato") +
                                  "*_ reagiu... "}
                              </MarkdownWrapper>
                              <Badge
                                className={classes.badge}
                                overlap="circular"
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "right"
                                }}
                                badgeContent={
                                  <span
                                    style={{
                                      fontSize: "1.2em",
                                      marginTop: "0",
                                      marginLeft: "5px"
                                    }}
                                  >
                                    {message.body}
                                  </span>
                                }
                              ></Badge>
                            </span>
                          </>
                        )}

                      <span className={classes.timestamp}>
                        {message.isEdited
                          ? "Editada " +
                            format(parseISO(message.createdAt), "HH:mm")
                          : format(parseISO(message.createdAt), "HH:mm")}
                        {renderMessageAck(message)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Diga olá para seu novo contato!</div>;
    }
  };

  // Função para gerar protocolo no formato AAAAMMDDHHMMSS + random
  function generateProtocol() {
    const now = new Date();
    const pad = n => n.toString().padStart(2, "0");
    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hour = pad(now.getHours());
    const min = pad(now.getMinutes());
    const sec = pad(now.getSeconds());
    const rand = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `${year}${month}${day}${hour}${min}${sec}${rand}`;
  }

  // Função para exportar PDF
  const handleExportPdf = async () => {
    console.log("PDF export function called");
    if (!messagesList.length) {
      console.log("No messages to export");
      return;
    }

    try {
      // Pega o número do protocolo já gerado na conversa no padrão AAAAMMDDHHMMSS
      let protocolo = "";
      if (ticket?.protocol && /^\d{14,}$/.test(ticket.protocol)) {
        protocolo = ticket.protocol;
      } else if (ticket?.protocolo && /^\d{14,}$/.test(ticket.protocolo)) {
        protocolo = ticket.protocolo;
      } else {
        protocolo = generateProtocol();
        console.log("Generated new protocol:", protocolo);
      }

      // Nome da entidade (empresa)
      const nomeEntidade = user?.company?.name || "Entidade não disponível";

      // Cria um container oculto para renderizar o conteúdo
      const pdfContainer = document.createElement("div");
      pdfContainer.style.position = "fixed";
      pdfContainer.style.left = "-9999px";
      pdfContainer.style.top = "0";
      pdfContainer.style.width = "800px";
      pdfContainer.style.background = "#fff";
      pdfContainer.style.padding = "24px";
      pdfContainer.innerHTML = `
      <div style="text-align:center;margin-bottom:16px;">
        <img src="${getPublicUrl("public/logotipos/interno.png")}" alt="Logo" style="max-width:120px;max-height:60px;"/>
        <h2 style="margin:8px 0 0 0;">${nomeEntidade}</h2>
      </div>
      <div style="margin-bottom:12px;">
        
        <strong>Data de Geração:</strong> ${new Date(
          ticket?.createdAt || Date.now()
        ).toLocaleDateString()} às ${new Date(
        ticket?.createdAt || Date.now()
      ).toLocaleTimeString()}<br/>
        <strong>Solicitante:</strong> ${ticket?.contact?.name || "-"}<br/>
        <strong>Contato (WhatsApp):</strong> ${ticket?.contact?.number || "-"}
      </div>
      <h3>Histórico da Conversa</h3>
      <div style="font-size:13px;">
        ${
          messagesList
            .map(msg => {
              const nomeUsuario =
                (msg.user && msg.user.name) ||
                (msg.sender && msg.sender.name) ||
                (msg.contact && msg.contact.name) ||
                "-";
              const textoMsg = msg.body || "[mensagem de mídia]";
              const dataHora = `${new Date(
                msg.createdAt
              ).toLocaleDateString()} ${new Date(
                msg.createdAt
              ).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit"
              })}`;
              return `${dataHora} – ${nomeUsuario}: ${textoMsg}`;
            })
            .join("<br/>") || "<i>Sem mensagens</i>"
        }
      </div>
    `;
      document.body.appendChild(pdfContainer);

      const canvas = await html2canvas(pdfContainer, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "pt", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pageWidth - 40;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 20, 20, pdfWidth, pdfHeight);
      // Store the PDF blob in a global variable for later use
      window.lastGeneratedPdf = pdf;
      // Save the PDF
      pdf.save(`protocolo_${protocolo}.pdf`);
      document.body.removeChild(pdfContainer);
      console.log("PDF generated successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  useEffect(() => {
    console.log("Registering global PDF export function");
    window.handleExportPdfGlobal = handleExportPdf;

    return () => {
      console.log("Unregistering global PDF export function");
      window.handleExportPdfGlobal = null;
    };
  }, [messagesList, ticket, ticketId]);

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
        {contactPresence === "composing" && (
          <div className={classes.messageLeft}>
            <div className={classes.wave}>
              <span className={classes.dot}></span>
              <span className={classes.dot}></span>
              <span className={classes.dot}></span>
            </div>
          </div>
        )}
        {contactPresence === "recording" && (
          <div className={classes.messageLeft}>
            <div className={classes.wavebarsContainer}>
              <div className={clsx(classes.wavebars, classes.wavebar1)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar2)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar3)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar4)}></div>
              <div className={clsx(classes.wavebars, classes.wavebar5)}></div>
            </div>
          </div>
        )}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
      <MediaViewerModal
        open={mediaViewerOpen}
        onClose={() => setMediaViewerOpen(false)}
        mediaList={mediaMessages}
        initialIndex={mediaViewerIndex}
        contact={ticket?.contact}
      />
    </div>
  );
};

export default MessagesList;
